/**
 * @file GraficoMotivoDescarte.jsx
 * @description Componente que exibe um gráfico de pizza mostrando a distribuição
 * dos motivos de descarte de leads. Apresenta os dados em formato visual e também
 * mostra o número total de leads descartados.
 */

import { Box, CircularProgress, IconButton, Tooltip, Typography } from "@mui/material";
import HelpOutlineTwoToneIcon from "@mui/icons-material/HelpOutlineTwoTone";
import MotivoDeDescarteGrafico from "../Charts/MotivoDeDescartePieChart";

/**
 * Componente GraficoMotivoDescarte
 * 
 * Exibe um gráfico de pizza com os motivos de descarte de leads, incluindo o total
 * de leads descartados. Se não houver dados de descarte, exibe uma mensagem alternativa.
 */
const GraficoMotivoDescarte = ({ aggregatedDiscardData, loading = false, tooltipText = "" }) => {
  const totalDescartados = aggregatedDiscardData.data.reduce((acc, val) => acc + val, 0);

  return (
    <Box sx={{
      height: '100%',
      p: 1.5,
      display: 'flex',
      flexDirection: 'column',
      boxShadow: 2,
      borderRadius: 2,
      bgcolor: 'background.paper',
      border: (theme) => `1px solid ${theme.palette.divider}`,
      minHeight: 400,
    }}>
      {/* Header do Gráfico */}
      <Box sx={{ mb: { xs: 2, md: 3 } }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            color: 'text.primary',
            fontSize: { xs: '1.125rem', md: '1.25rem' },
            mb: 0.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1
          }}
        >
          Motivos de Descarte
          {tooltipText?.trim() && (
            <Tooltip
              title={tooltipText}
              placement="top"
              componentsProps={{
                tooltip: {
                  sx: { fontSize: "0.95rem", lineHeight: 1.4, p: 1.25 },
                },
              }}
            >
              <IconButton
                size="medium"
                aria-label="Ajuda do grafico de motivos de descarte"
                sx={{ p: 0.75 }}
              >
                <HelpOutlineTwoToneIcon sx={{ fontSize: 22 }} />
              </IconButton>
            </Tooltip>
          )}
        </Typography>
      </Box>

      {/* Renderização condicional: verifica se existem dados para exibir */}
      {aggregatedDiscardData.data.length > 0 && totalDescartados > 0 ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
          {/* Container do Gráfico */}
          <Box
            sx={{
              flex: 1,
              minHeight: { xs: 200, md: 280 },
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              background: 'linear-gradient(135deg, rgba(244, 67, 54, 0.02) 0%, rgba(233, 30, 99, 0.02) 100%)',
              borderRadius: 2,
              position: 'relative',
            }}
            aria-busy={loading ? 'true' : 'false'}
            aria-label="Gráfico de pizza com estatísticas de descartes por categoria"
          >
            {/* Componente do gráfico de pizza que recebe os dados agregados */}
            <MotivoDeDescarteGrafico aggregatedDiscardData={aggregatedDiscardData} />
            {loading && (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: (theme) =>
                    theme.palette.mode === 'dark' ? 'rgba(11, 16, 32, 0.55)' : 'rgba(255, 255, 255, 0.45)',
                  pointerEvents: 'none',
                }}
              >
                <CircularProgress size={32} />
              </Box>
            )}
          </Box>

          {/* Card de Total Descartados */}
          <Box
            sx={{
              textAlign: 'center'
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'center',
                alignItems: 'center',
                gap: 1
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  color: 'text.secondary',
                  fontSize: '1rem'
                }}
              >
                Total Descartados:
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: '#f44336',
                  fontSize: '1rem'
                }}
              >
                {totalDescartados.toLocaleString()}
              </Typography>
            </Box>
          </Box>
        </Box>
      ) : (
        // Estado vazio - quando não há dados de descarte para mostrar
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: { xs: 200, md: 280 },
            bgcolor: (theme) => theme.palette.action.hover,
            position: 'relative',
            borderRadius: 2,
            p: { xs: 3, md: 4 },
            textAlign: 'center'
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              fontSize: { xs: '0.875rem', md: '1rem' },
              maxWidth: 300,
              lineHeight: 1.5
            }}
          >
            Nenhum lead foi descartado no período selecionado.
          </Typography>
          {loading && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: (theme) =>
                  theme.palette.mode === 'dark' ? 'rgba(11, 16, 32, 0.55)' : 'rgba(255, 255, 255, 0.45)',
                pointerEvents: 'none',
              }}
            >
              <CircularProgress size={32} />
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default GraficoMotivoDescarte;
